import { Routes, Route, useLocation } from "react-router-dom";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { DashboardLayout } from "@/components/DashboardLayout";
import LandingPage from "@/pages/LandingPage";
import { AuthPage } from "@/pages/auth/AuthPage";
import { AuthCallback } from "@/pages/auth/AuthCallback";
import { ForgotPasswordPage } from "@/pages/auth/ForgotPasswordPage";
import { ResetPasswordPage } from "@/pages/auth/ResetPasswordPage";
import { HelpPage } from "@/pages/HelpPage";
import { ContactPage } from "@/pages/ContactPage";
import { PrivacyPolicyPage } from "@/pages/PrivacyPolicyPage";
import { TermsOfServicePage } from "@/pages/TermsOfServicePage";
import { PublicFormPage } from "@/pages/PublicFormPage";
import { AdminLogin } from "@/pages/admin/AdminLogin";
import { AdminDashboard } from "@/pages/admin/AdminDashboard";
import DashboardHome from "@/pages/dashboard/DashboardHome";
import ContentPage from "@/pages/dashboard/ContentPage";
import VideosPage from "@/pages/dashboard/VideosPage";
import CampaignsPage from "@/pages/dashboard/CampaignsPage";
import ComparisonPage from "@/pages/ComparisonPage";
import CaseStudyPage from "@/pages/CaseStudyPage";
import CalendarPage from "@/pages/dashboard/CalendarPage";
import FlyersPage from "@/pages/dashboard/FlyersPage";
import TemplatesPage from "@/pages/dashboard/TemplatesPage";
import AffiliatesPage from "@/pages/dashboard/AffiliatesPage";
import ReferralsPage from "@/pages/dashboard/ReferralsPage";
import LeadsPage from "@/pages/dashboard/LeadsPage";
import AnalyticsPage from "@/pages/dashboard/AnalyticsPage";
import SettingsPage from "@/pages/dashboard/SettingsPage";
import DashboardHelpPage from "@/pages/dashboard/DashboardHelpPage";
import WorkspacesPage from "@/pages/dashboard/WorkspacesPage";
import CitationsPage from "@/pages/dashboard/CitationsPage";
import PublicWorkspacePage from "@/pages/PublicWorkspacePage";
import PublicDemoPage from "@/pages/PublicDemoPage";
import IntegrationsPage from "@/pages/IntegrationsPage";
import TrustPage from "@/pages/TrustPage";
import PricingPage from "@/pages/PricingPage";
import DemoPage from "@/pages/DemoPage";
import SeoTemplatePage from "@/pages/SeoTemplatePage";
import TemplatesDirectoryPage from "@/pages/TemplatesDirectoryPage";
import NicheLandingPage from "@/pages/NicheLandingPage";
import SolutionsDirectoryPage from "@/pages/SolutionsDirectoryPage";
import GuidesIndexPage from "@/pages/GuidesIndexPage";
import GuidePage from "@/pages/GuidePage";
import GlossaryIndexPage from "@/pages/GlossaryIndexPage";
import GlossaryTermPage from "@/pages/GlossaryTermPage";
import CitationTrackerPage from "@/pages/CitationTrackerPage";
import LeaderboardPage from "@/pages/LeaderboardPage";
import WallOfWinsPage from "@/pages/WallOfWinsPage";
import VsCompetitorPage from "@/pages/VsCompetitorPage";
import GTMTracker from "@/components/GTMTracker";

const AppRouter = () => {
  const location = useLocation();
  
  // CRITICAL: Detect session_id during render (NOT in useEffect) to prevent race conditions.
  // Two paths land here:
  //   - Legacy Emergent flow: /...#session_id=...
  //   - Direct-Google flow:    /auth/callback?token=...  (or ?error=...)
  if (
    location.hash?.includes('session_id=') ||
    location.pathname === '/auth/callback'
  ) {
    return <AuthCallback />;
  }
  
  return (
    <>
      <GTMTracker />
      <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<AuthPage isLogin={true} />} />
      <Route path="/register" element={<AuthPage isLogin={false} />} />
      <Route path="/auth" element={<AuthPage isLogin={true} />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/help" element={<HelpPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsOfServicePage />} />
      <Route path="/form/:formId" element={<PublicFormPage />} />
      <Route path="/t/:slug" element={<SeoTemplatePage />} />
      <Route path="/templates/:slug" element={<SeoTemplatePage />} />
      <Route path="/templates" element={<TemplatesDirectoryPage />} />
      <Route path="/solutions" element={<SolutionsDirectoryPage />} />
      <Route path="/solutions/:nicheSlug" element={<NicheLandingPage />} />
      <Route path="/vs" element={<ComparisonPage />} />
      <Route path="/case-study" element={<CaseStudyPage />} />
      <Route path="/guides" element={<GuidesIndexPage />} />
      <Route path="/guides/:slug" element={<GuidePage />} />
      <Route path="/glossary" element={<GlossaryIndexPage />} />
      <Route path="/glossary/:slug" element={<GlossaryTermPage />} />
      <Route path="/citation-tracker" element={<CitationTrackerPage />} />
      <Route path="/leaderboard" element={<LeaderboardPage />} />
      <Route path="/wins" element={<WallOfWinsPage />} />
      <Route path="/vs/:slug" element={<VsCompetitorPage />} />
      <Route path="/w/:slug" element={<PublicWorkspacePage />} />
      <Route path="/d/:slug" element={<PublicDemoPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/integrations" element={<IntegrationsPage />} />
      <Route path="/trust" element={<TrustPage />} />
      <Route path="/demo" element={<DemoPage />} />
      <Route path="/admin" element={<AdminLogin />} />
      <Route path="/admin/dashboard" element={<AdminDashboard />} />
      <Route path="/dashboard" element={<ProtectedRoute><DashboardLayout><DashboardHome /></DashboardLayout></ProtectedRoute>} />
      <Route path="/dashboard/content" element={<ProtectedRoute><DashboardLayout><ContentPage /></DashboardLayout></ProtectedRoute>} />
      <Route path="/dashboard/videos" element={<ProtectedRoute><DashboardLayout><VideosPage /></DashboardLayout></ProtectedRoute>} />
      <Route path="/dashboard/campaigns" element={<ProtectedRoute><DashboardLayout><CampaignsPage /></DashboardLayout></ProtectedRoute>} />
      <Route path="/dashboard/calendar" element={<ProtectedRoute><DashboardLayout><CalendarPage /></DashboardLayout></ProtectedRoute>} />
      <Route path="/dashboard/flyers" element={<ProtectedRoute><DashboardLayout><FlyersPage /></DashboardLayout></ProtectedRoute>} />
      <Route path="/dashboard/templates" element={<ProtectedRoute><DashboardLayout><TemplatesPage /></DashboardLayout></ProtectedRoute>} />
      <Route path="/dashboard/affiliates" element={<ProtectedRoute><DashboardLayout><AffiliatesPage /></DashboardLayout></ProtectedRoute>} />
      <Route path="/dashboard/leads" element={<ProtectedRoute><DashboardLayout><LeadsPage /></DashboardLayout></ProtectedRoute>} />
      <Route path="/dashboard/referrals" element={<ProtectedRoute><DashboardLayout><ReferralsPage /></DashboardLayout></ProtectedRoute>} />
      <Route path="/dashboard/analytics" element={<ProtectedRoute><DashboardLayout><AnalyticsPage /></DashboardLayout></ProtectedRoute>} />
      <Route path="/dashboard/help" element={<ProtectedRoute><DashboardLayout><DashboardHelpPage /></DashboardLayout></ProtectedRoute>} />
      <Route path="/dashboard/settings" element={<ProtectedRoute><DashboardLayout><SettingsPage /></DashboardLayout></ProtectedRoute>} />
      <Route path="/dashboard/workspaces" element={<ProtectedRoute><DashboardLayout><WorkspacesPage /></DashboardLayout></ProtectedRoute>} />
      <Route path="/dashboard/citations" element={<ProtectedRoute><DashboardLayout><CitationsPage /></DashboardLayout></ProtectedRoute>} />
      </Routes>
    </>
  );
};

export default AppRouter;
